import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import { useAuth } from './AuthContext';
import { convertCurrency } from '../utils/currencyUtils';

const AppContext = createContext();

const isValidUUID = (uuid) => {
  if (!uuid || typeof uuid !== 'string') return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

const prepareForSupabase = (obj) => {
  const result = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    let value = obj[key];
    const isIdField = snakeKey === 'id' || snakeKey === 'user_id' || snakeKey.endsWith('_id');
    if (isIdField) {
      const stringValue = value ? String(value).trim() : '';
      if (!isValidUUID(stringValue)) {
        value = null;
      } else {
        value = stringValue;
      }
    }
    if (['recurring_amount', 'upfront_payment', 'ai_credit_amount', 'amount'].includes(snakeKey)) {
      const parsed = parseFloat(value);
      value = isNaN(parsed) ? 0 : parsed;
    }
    if ((value === '' || value === undefined) && (snakeKey.endsWith('_at') || snakeKey.endsWith('_date'))) {
      value = null;
    }
    result[snakeKey] = value;
  }
  return result;
};

const mapToJS = (item) => {
  if (!item) return item;
  const mapped = {};
  for (const key in item) {
    const camelKey = key.replace(/([-_][a-z])/g, (group) =>
      group.toUpperCase().replace('-', '').replace('_', '')
    );
    mapped[camelKey] = item[key];
  }
  return mapped;
};

export const AppProvider = ({ children }) => {
  const { user, profile, refreshProfile } = useAuth();
  const [state, setState] = useState({
    paymentMethods: [],
    categories: [],
    subscriptions: [],
    receipts: [],
    exchangeRates: {},
    settings: {
      email: '',
      frequency: 'Weekly',
      preferredCurrency: 'USD'
    },
  });

  useEffect(() => {
    if (user) {
      fetchAllData();
      updateExchangeRates();
    }
  }, [user]);

  useEffect(() => {
    if (profile) {
      setState((s) => ({
        ...s,
        settings: {
          ...s.settings,
          frequency: profile.report_frequency || 'Weekly',
          email: profile.email,
          preferredCurrency: profile.preferred_currency || 'USD'
        },
      }));
    }
  }, [profile]);

  const updateExchangeRates = async () => {
    try {
      // Fetch latest from public API
      const response = await fetch('https://open.er-api.com/v6/latest/USD');
      const data = await response.json();
      
      if (data && data.rates) {
        // Update local state
        setState(prev => ({ ...prev, exchangeRates: data.rates }));
        
        // Update DB cache for offline/admin use
        await supabase.from('exchange_rates_20240520').upsert({
          base_currency: 'USD',
          rates: data.rates,
          updated_at: new Date().toISOString()
        }, { onConflict: 'base_currency' });
      }
    } catch (err) {
      console.error('Failed to update exchange rates:', err);
      // Fallback to DB cache
      const { data } = await supabase.from('exchange_rates_20240520').select('rates').single();
      if (data) setState(prev => ({ ...prev, exchangeRates: data.rates }));
    }
  };

  const fetchAllData = async () => {
    if (!user) return;
    try {
      const [pm, cat, sub, rec, rates] = await Promise.all([
        supabase.from('payment_methods_20240520').select('*').eq('user_id', user.id),
        supabase.from('categories_20240520').select('*').eq('user_id', user.id),
        supabase.from('subscriptions_20240520').select('*').eq('user_id', user.id),
        supabase.from('receipts_20240520').select('*').eq('user_id', user.id),
        supabase.from('exchange_rates_20240520').select('rates').single()
      ]);

      setState((s) => ({
        ...s,
        paymentMethods: (pm.data || []).map(mapToJS),
        categories: (cat.data || []).map(mapToJS),
        subscriptions: (sub.data || []).map(mapToJS),
        receipts: (rec.data || []).map(mapToJS),
        exchangeRates: rates.data?.rates || s.exchangeRates
      }));
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  const actions = {
    updateSettings: async (newSettings) => {
      if (!user) return;
      try {
        const updateData = {};
        if (newSettings.frequency) updateData.report_frequency = newSettings.frequency;
        if (newSettings.preferredCurrency) updateData.preferred_currency = newSettings.preferredCurrency;

        const { error } = await supabase
          .from('profiles_20240520')
          .update(updateData)
          .eq('id', user.id);

        if (error) throw error;
        await refreshProfile();
      } catch (err) {
        console.error('Update settings error:', err);
      }
    },
    // ... rest of existing actions
    addPaymentMethod: async (pm) => {
      const dbData = prepareForSupabase({ ...pm, user_id: user.id });
      delete dbData.id;
      const { data } = await supabase.from('payment_methods_20240520').insert([dbData]).select();
      if (data) fetchAllData();
    },
    updatePaymentMethod: async (id, pm) => {
      if (!isValidUUID(id)) return;
      const dbData = prepareForSupabase(pm);
      delete dbData.id;
      await supabase.from('payment_methods_20240520').update(dbData).eq('id', id);
      fetchAllData();
    },
    deletePaymentMethod: async (id) => {
      if (!isValidUUID(id)) return;
      await supabase.from('payment_methods_20240520').delete().eq('id', id);
      fetchAllData();
    },
    addCategory: async (cat) => {
      const dbData = prepareForSupabase({ ...cat, user_id: user.id });
      delete dbData.id;
      const { data } = await supabase.from('categories_20240520').insert([dbData]).select();
      if (data) fetchAllData();
    },
    updateCategory: async (id, cat) => {
      if (!isValidUUID(id)) return;
      const dbData = prepareForSupabase(cat);
      delete dbData.id;
      await supabase.from('categories_20240520').update(dbData).eq('id', id);
      fetchAllData();
    },
    deleteCategory: async (id) => {
      if (!isValidUUID(id)) return;
      await supabase.from('categories_20240520').delete().eq('id', id);
      fetchAllData();
    },
    addSubscription: async (sub) => {
      const dbData = prepareForSupabase({ ...sub, user_id: user.id, status: 'active' });
      delete dbData.id;
      const { data, error } = await supabase.from('subscriptions_20240520').insert([dbData]).select();
      if (error) alert(`Error: ${error.message}`);
      if (data) fetchAllData();
    },
    updateSubscription: async (id, sub) => {
      if (!isValidUUID(id)) return;
      const dbData = prepareForSupabase(sub);
      delete dbData.id;
      await supabase.from('subscriptions_20240520').update(dbData).eq('id', id);
      fetchAllData();
    },
    deleteSubscription: async (id) => {
      if (!isValidUUID(id)) return;
      await supabase.from('subscriptions_20240520').delete().eq('id', id);
      fetchAllData();
    },
    cancelSubscription: async (id) => {
      if (!isValidUUID(id)) return;
      await supabase
        .from('subscriptions_20240520')
        .update({ status: 'cancelled', cancelled_at: new Date().toISOString() })
        .eq('id', id);
      fetchAllData();
    },
    unCancelSubscription: async (id) => {
      if (!isValidUUID(id)) return;
      await supabase
        .from('subscriptions_20240520')
        .update({ status: 'active', cancelled_at: null })
        .eq('id', id);
      fetchAllData();
    },
    addReceipt: async (receipt) => {
      const dbData = prepareForSupabase({ ...receipt, user_id: user.id });
      delete dbData.id;
      const { data } = await supabase.from('receipts_20240520').insert([dbData]).select();
      if (data) fetchAllData();
    },
    deleteReceipt: async (id) => {
      if (!isValidUUID(id)) return;
      await supabase.from('receipts_20240520').delete().eq('id', id);
      fetchAllData();
    },
    refresh: fetchAllData,
  };

  return <AppContext.Provider value={{ state, ...actions }}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext(AppContext);